
import React from 'react';
import { Card, Spinner, Button, Tooltip } from '@dopomogai/ui';
import { TrendingUp, FileText } from 'lucide-react';
import { useAgentUsage } from '@dopomogai/agent-kit';

interface UsageStatsCardProps {
  agentId: string;
}

export const UsageStatsCard: React.FC<UsageStatsCardProps> = ({ agentId }) => {
  const { usage, isLoading, error } = useAgentUsage(agentId);

  const StatItem: React.FC<{ value: string; label: string }> = ({ value, label }) => (
    <div className="text-center">
      <p className="text-xl font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-24">
          <Spinner text="Loading stats..." />
        </div>
      );
    }
    if (error) {
      return (
        <div className="p-4">
          <Card noPadding className="bg-rose-50 border-rose-200 text-rose-700 p-3 text-sm">
            Could not load usage. {error.message}
          </Card>
        </div>
      );
    }
    if (usage) {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-4 pt-1">
            <StatItem value={usage.total_credits_charged.toFixed(2)} label="Credits Used" />
            <StatItem value={(usage.total_prompt_tokens / 1000).toFixed(1) + 'k'} label="Input Tokens" />
            <StatItem value={(usage.total_completion_tokens / 1000).toFixed(1) + 'k'} label="Output Tokens" />
          </div>
          <div className="border-t border-slate-200 pt-3">
            <Tooltip text="Detailed reporting coming soon.">
              <Button
                text="View Full Report"
                variant="secondary"
                size="sm"
                icon={<FileText size={14} />}
                disabled
                className="w-full justify-center"
              />
            </Tooltip>
          </div>
        </div>
      );
    }
    return (
      <div className="text-center text-slate-500 py-8 text-sm">
        No usage data available for this period.
      </div>
    );
  };

  return (
    <Card
      title="Usage (This Month)"
      icon={<TrendingUp size={18} />}
      headerBgColor="bg-emerald-100"
      className="space-y-2"
    >
      {renderContent()}
    </Card>
  );
};